/** One geometry for both modes (Zero-Re-Layout): the view of a stored list and its
 *  edit-mode widget render the same rows — a text line in view mode, a small input in
 *  edit mode, both ROW_PX high, the type in the same column — and the same reserved add
 *  row under the list (the add control in edit mode, empty in view mode). */
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export const ROW_PX = 32;
export const ADD_ROW_PX = 40;
export const ROW_SX = { display: 'flex', alignItems: 'center', gap: 0.5, height: ROW_PX, minWidth: 0 } as const;
export const TYPE_COL = { width: 72, flex: 'none' } as const;
/** Room for "Required (always)" on one line beside its icon (it wrapped at 112). */
export const REQUIRED_COL = { width: 136, flex: 'none', display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 } as const;
export const ACTIONS_COL = { width: 100, flex: 'none', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' } as const;
export const INPUT_SX = {
  flex: '1 1 auto',
  minWidth: 0,
  '& .MuiInputBase-root': { height: 28, fontSize: '0.875rem' },
  '& .MuiInputBase-input': { py: 0, px: 1 },
} as const;

/** "Couldn’t load … — Retry" under a list whose names come from the operator's lists. */
export function ListFailure({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <Typography variant="caption" color="error" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }} data-list-error>
      <ErrorOutlineIcon aria-hidden sx={{ fontSize: 14 }} />
      Couldn’t load {what} —
      <Button size="small" color="inherit" onClick={onRetry} sx={{ minWidth: 0, py: 0, fontSize: 12 }}>
        Retry
      </Button>
    </Typography>
  );
}

/** The add row reserved under a list in both modes (the add control in edit mode). */
export function AddRow({ children }: { children?: ReactNode }) {
  return (
    <Box data-precheck-add sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: ADD_ROW_PX, minWidth: 0 }}>
      {children}
    </Box>
  );
}

/** A muted, locked note on a row of its own ("Same as the lead booker’s …"). */
export function NoteRow({ children }: { children: ReactNode }) {
  return (
    <Box sx={ROW_SX} data-precheck-note>
      <LockOutlinedIcon aria-hidden sx={{ fontSize: 14, color: 'text.secondary' }} />
      <Typography variant="caption" color="text.secondary" noWrap>
        {children}
      </Typography>
    </Box>
  );
}

/** A field's Required flag in view mode: an icon and words. */
export function RequiredText({ on, always }: { on: boolean; always?: boolean }) {
  return (
    <>
      {on ? <CheckIcon aria-hidden sx={{ fontSize: 16, color: 'primary.main' }} /> : <RemoveIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />}
      <Typography variant="caption" color={on ? 'text.primary' : 'text.secondary'} noWrap>
        {on ? (always ? 'Required (always)' : 'Required') : 'Optional'}
      </Typography>
    </>
  );
}
